import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

export default function HeroSlider({
  company,
  scrollToSection,
  slides: slidesProp,
}) {
  const slides = useMemo(() => {
    // Falls du (noch) nichts übergibst: leeres Array vermeiden
    if (Array.isArray(slidesProp) && slidesProp.length > 0) return slidesProp;
    return [
      {
        id: 1,
        imageUrl: '/seo-assets/elektroverteilung-konstanz.jpg',
        alt: 'Elektrotechnik – Verteilung / Schaltschrank im Aufbau',
        titleText: 'EOS Energy Systems – Elektrotechnische Systeme',
        headline: ['Elektrotechnische Systeme.', 'Keine Einzelmaßnahmen.'],
        subline:
          'Planung, Integration und Betrieb – strukturiert, nachvollziehbar, dauerhaft betreibbar.',
        cta: { label: 'Projekt besprechen', targetId: 'contact' },
        footerLine: 'companyName',
      },
    ];
  }, [slidesProp]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef(null);
  const SLIDE_INTERVAL = 10000; // 10 s

  const stopAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoSlide = useCallback(() => {
    stopAutoSlide();
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, SLIDE_INTERVAL);
  }, [stopAutoSlide, slides.length]);

  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide();
    // slides.length bewusst als Trigger: wenn du im Mock Slides änderst, passt der Loop
  }, [startAutoSlide, stopAutoSlide]);

  const handleManualChange = (newIndex) => {
    setCurrentSlide(newIndex);
    startAutoSlide();
  };

  function useFitTextLongestWord({
    minPx = 22,
    maxPx = 60,
    deps = [],
    text = '',
  } = {}) {
    const boxRef = useRef(null);
    const textRef = useRef(null);
    const [fontPx, setFontPx] = useState(maxPx);

    useLayoutEffect(() => {
      const box = boxRef.current;
      const el = textRef.current;
      if (!box || !el) return;

      let raf = 0;

      const getLongestWordWidthAt = (px) => {
        const cs = window.getComputedStyle(el);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const font = `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${px}px ${cs.fontFamily}`;
        ctx.font = font;

        // letter-spacing -> px
        const lsRaw = cs.letterSpacing;
        let letterSpacingPx = 0;

        if (lsRaw && lsRaw !== 'normal') {
          if (lsRaw.endsWith('px')) letterSpacingPx = parseFloat(lsRaw) || 0;
          else if (lsRaw.endsWith('em'))
            letterSpacingPx = (parseFloat(lsRaw) || 0) * px;
          else if (lsRaw.endsWith('rem')) {
            const rootPx =
              parseFloat(getComputedStyle(document.documentElement).fontSize) ||
              16;
            letterSpacingPx = (parseFloat(lsRaw) || 0) * rootPx;
          }
        }

        const words = String(text)
          .replace(/[.,;:!?]/g, '') // ← WICHTIG
          .replace(/\s+/g, ' ')
          .trim()
          .split(' ')
          .filter(Boolean);

        let maxW = 0;
        for (const w of words) {
          // measureText + letter-spacing (zwischen Zeichen)
          const base = ctx.measureText(w).width;
          const extra = Math.max(0, w.length - 1) * letterSpacingPx;
          const width = base + extra;

          if (width > maxW) maxW = width;
        }
        return maxW;
      };

      const fit = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const csBox = window.getComputedStyle(box);
          const padL = parseFloat(csBox.paddingLeft) || 0;
          const padR = parseFloat(csBox.paddingRight) || 0;

          // clientWidth enthält Padding, aber nicht Border.
          // Nutzbare Textbreite = clientWidth - Padding links/rechts - Sicherheitsreserve
          const SAFETY_PX = 1;
          const boxWidth = Math.floor(
            box.clientWidth - padL - padR - SAFETY_PX
          );

          if (!boxWidth) return;

          // 1) Prüfe maxPx
          let longestAtMax = getLongestWordWidthAt(maxPx);
          if (longestAtMax <= boxWidth) {
            setFontPx(maxPx);
            return;
          }

          // 2) Binärsuche zwischen min/max: finde größte Größe, bei der längstes Wort passt
          let lo = minPx;
          let hi = maxPx;

          for (let i = 0; i < 14; i++) {
            const mid = (lo + hi) / 2;
            const longest = getLongestWordWidthAt(mid);

            if (longest > boxWidth) hi = mid;
            else lo = mid;
          }

          const size = Math.max(minPx, Math.min(maxPx, lo));
          setFontPx(size);
        });
      };

      fit();
      const ro = new ResizeObserver(fit);
      ro.observe(box);

      const t = setTimeout(fit, 200);

      return () => {
        clearTimeout(t);
        ro.disconnect();
        cancelAnimationFrame(raf);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { boxRef, textRef, fontPx };
  }

  const nextSlide = () =>
    handleManualChange((currentSlide + 1) % slides.length);
  const prevSlide = () =>
    handleManualChange((currentSlide - 1 + slides.length) % slides.length);

  const SlideOverlay = ({ slide }) => {
    const headlineA = Array.isArray(slide.headline)
      ? slide.headline[0]
      : slide.headline;
    const headlineB = Array.isArray(slide.headline) ? slide.headline[1] : null;

    const measureText = `${headlineA ?? ''} ${headlineB ?? ''}`.trim();

    const { boxRef, textRef, fontPx } = useFitTextLongestWord({
      minPx: 10,
      maxPx: 60,
      text: measureText,
      deps: [currentSlide, measureText],
    });

    const footerLine =
      slide.footerLine === 'companyName'
        ? company?.name
          ? company.name
          : 'EOS Energy Systems GmbH'
        : slide.footerLine;

    return (
      <>
        <div className="absolute inset-0 bg-gradient-to-r from-eos-grauDunkel/85 via-eos-grauDunkel/55 to-eos-grauDunkel/10" />
        <div className="absolute inset-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]" />

        <div className="relative z-10 h-full">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-full">
            <div className="h-full flex items-start pt-28 sm:pt-32 lg:pt-0 lg:items-center lg:justify-start">
              <div
                ref={boxRef}
                className={[
                  'min-w-[15rem]',
                  'w-full max-w-[calc(50vw-1rem)] sm:max-w-[calc(50vw-1.5rem)]',
                  'lg:max-w-[42rem]',
                  'border border-white/10 bg-eos-grauDunkel/65 backdrop-blur-[2px] rounded-none',
                  'p-5 sm:p-6 lg:p-8',
                  'max-h-[72vh] overflow-y-scroll',
                ].join(' ')}
              >
                <h1
                  ref={textRef}
                  className="text-eos-weißFast font-semibold tracking-tight leading-[1.08] whitespace-normal break-normal hyphens-none"
                  style={{ fontSize: fontPx }}
                >
                  {headlineA}
                  {headlineB ? (
                    <>
                      <br />
                      {headlineB}
                    </>
                  ) : null}
                </h1>

                {slide.subline ? (
                  <p className="mt-4 sm:mt-5 text-eos-weißFast/75 text-[clamp(0.95rem,3.4vw,1.125rem)]">
                    {slide.subline.split('\n').map((line, i) => (
                      <span key={i}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </p>
                ) : null}

                {slide.cta?.label && slide.cta?.targetId ? (
                  <div className="mt-6 sm:mt-7">
                    <Button
                      size="lg"
                      className="bg-eos-weißFast text-eos-grauDunkel hover:bg-eos-weißFaster rounded-none px-3 py-2 text-[clamp(0.875rem,2.8vw,0.5rem)]"
                      onClick={() => scrollToSection?.(slide.cta.targetId)}
                    >
                      {slide.cta.label}
                    </Button>
                  </div>
                ) : footerLine ? (
                  <div className="mt-6 text-sm text-eos-weißFast/50">
                    {footerLine}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <section
      id="hero"
      data-bg="dark"
      className="relative min-h-[100svh] w-full overflow-hidden bg-eos-grauDunkel"
    >
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id ?? index}
          className={`absolute inset-0 ${
            index === currentSlide ? 'opacity-100 z-20' : 'opacity-0 z-10'
          }`}
          style={{ transition: 'opacity 1200ms ease-in-out' }}
        >
          {/* Hintergrundbild (KenBurns / Zoom bleibt) */}
          <img
            src={slide.imageUrl}
            alt={slide.alt}
            title={slide.titleText}
            className="absolute inset-0 w-full h-full object-cover object-center animate-kenburns"
            loading={index === 0 ? 'eager' : 'lazy'}
          />

          {/* Einheitliches CI-Layout über alle Slides */}
          <SlideOverlay slide={slide} />
        </div>
      ))}

      {/* Navigation */}
      {slides.length > 1 && (
        <>
          {/* Indikatoren */}
          <div className="absolute bottom-5 w-full flex justify-center space-x-2 z-30">
            {slides.map((_, idx) => (
              <button
                key={idx}
                aria-label={`Slide ${idx + 1}`}
                onClick={() => handleManualChange(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx === currentSlide
                    ? 'bg-eos-weißFast scale-110'
                    : 'bg-white/35'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
